// utils/ErrorTracker.jsx
import axios from 'axios';
import logger from './UnifiedLogger';
import { API_ENDPOINTS } from './constants';
import {
    getBrowserInfo,
    getDeviceInfo,
    getClientIP,
    getSessionInfo,
    getErrorSeverity,
    getNetworkInfo,
    getPageInfo
} from './util';

class ErrorTracker {
    constructor() {
        this.config = {
            url: API_ENDPOINTS.logger_url,
            timeout: 10000, // 10 seconds for logging APIs
            maxRetries: 3,
            enabled: true,
        };

        this.isProcessing = false;

        // Create dedicated axios instance for error tracking that won't be affected by abort controllers
        this.loggerAxios = axios.create({
            timeout: this.config.timeout,
            headers: {
                'Content-Type': 'application/json',
                'X-Error-Tracker': 'frontend-api-errors',
            }
        });

        // Setup interceptors for the logger axios instance
        this.setupLoggerInterceptors();
    }

    setupLoggerInterceptors() {
        // Request interceptor
        this.loggerAxios.interceptors.request.use(
            (config) => {
                config.metadata = { startTime: Date.now() };
                return config;
            },
            (error) => {
                logger.warning("ERROR_TRACKER", "Request interceptor error", {
                    error: error.message
                });
                return Promise.reject(error);
            }
        );

        // Response interceptor
        this.loggerAxios.interceptors.response.use(
            (response) => {
                const duration = Date.now() - response.config.metadata.startTime;
                logger.debug("ERROR_TRACKER", "API error sent successfully", {
                    url: response.config.url,
                    status: response.status,
                    duration: `${duration}ms`
                });
                return response;
            },
            (error) => {
                const duration = error.config?.metadata?.startTime
                    ? Date.now() - error.config.metadata.startTime
                    : 0;

                logger.warning("ERROR_TRACKER", "Failed to send API error", {
                    url: error.config?.url,
                    status: error.response?.status,
                    duration: `${duration}ms`,
                    error: error.message
                });
                return Promise.reject(error);
            }
        );
    }

    normalize = (value, fallback = null) => value ?? fallback;

    isCancelledError = (code) =>
        code === "ERR_CANCELED" ||
        code === "ALL_REQUESTS_CANCELLED" ||
        (typeof code === "string" &&
            (code.includes("cancelled") || code.includes("aborted")));

    buildApiErrorDetails = (apiError) => ({
        type: "API_ERROR",
        request_id: this.normalize(apiError.requestId),
        method: this.normalize(apiError.method, "Unknown"),
        url: this.normalize(apiError.url, "Unknown"),
        status: this.normalize(apiError.status),
        status_text: this.normalize(apiError.statusText),
        response_data: this.normalize(apiError.responseData),
        error_message: this.normalize(apiError.message, "No message provided"),
        error_code: this.normalize(apiError.code),
        duration: this.normalize(apiError.duration),
        attempts: this.normalize(apiError.attempts, 1),
        timeout: this.normalize(apiError.timeout),
        curl: this.normalize(apiError.curl),
        headers: this.normalize(apiError.headers),
        request_data: this.normalize(apiError.requestData),
        component: this.normalize(apiError.component, "Unknown"),
        severity: getErrorSeverity(apiError.status),
        is_timeout: apiError.code === "ECONNABORTED",
        is_network_error: apiError.code === "NETWORK_ERROR",
        is_cancelled: this.isCancelledError(apiError.code),
        retry_info: {
            max_retries: this.normalize(apiError.maxRetries, 0),
            current_attempt: this.normalize(apiError.attempts, 1),
            should_retry: this.normalize(apiError.shouldRetry, false),
            retry_delay: this.normalize(apiError.retryDelay),
        }
    });




    // Construct API error object
    async constructApiErrorObject(apiError) {
        const now = new Date();
        const { browser, browserVersion } = getBrowserInfo();
        const deviceInfo = getDeviceInfo();
        const sessionInfo = getSessionInfo();
        const networkInfo = getNetworkInfo();
        const pageInfo = getPageInfo();
        const clientIP = await getClientIP();

        return {
            // Timestamp info
            timestamp: now.getTime(),
            time: now.toISOString(),

            // Environment info
            ip: clientIP,
            browser,
            browser_version: browserVersion,
            user_agent: deviceInfo.userAgent,
            device: deviceInfo.device,
            os: deviceInfo.os,
            screen_resolution: `${deviceInfo.screen.width}x${deviceInfo.screen.height}`,
            viewport_size: `${deviceInfo.viewport.width}x${deviceInfo.viewport.height}`,

            // Page info
            url: pageInfo.url,
            pathname: pageInfo.pathname,
            referrer: pageInfo.referrer,
            language: pageInfo.language,
            timezone: pageInfo.timezone,

            // Session info
            session_id: sessionInfo.sessionId,
            user_id: sessionInfo.userId,
            visit_id: sessionInfo.visitId,

            // Network status
            network_status: networkInfo.network_status,
            connection: networkInfo.connection,

            // API Error Details
            api_error: this.buildApiErrorDetails(apiError),
        };
    }

    getErrorSeverity(status) {
        if (!status) return "error";
        if (status >= 500) return "critical";
        if (status >= 400) return "error";
        if (status >= 300) return "warning";
        return "info";
    }

    async sendError(errorObject) {
        let error = [];
        error.push(errorObject);
        const payload = {
            user_id: errorObject.user_id,
            timestamp: Date.now(),
            errors: JSON.stringify(error)
        };

        const response = await this.loggerAxios.post(this.config.url, payload);

        if (response.status >= 400) {
            throw new Error(`Server responded with status ${response.status}`);
        }

        return response;
    }

    // Main method to track API errors
    async trackApiError(apiErrorDetails) {
        if (!this.config.enabled) {
            logger.info("ERROR_TRACKER", "API error tracking disabled", {
                url: apiErrorDetails.url
            });
            return;
        }

        if (this.isProcessing) {
            logger.info("ERROR_TRACKER", "Already processing error, skipping", {
                url: apiErrorDetails.url
            });
            return;
        }

        if (apiErrorDetails.curl.includes("No active or queued requests to cancel")) {
            logger.info("ERROR_TRACKER", "No active or queued requests to cancel", {
                curl: apiErrorDetails.curl
            });
            return;
        }

        this.isProcessing = true;

        try {

            const errorObject = await this.constructApiErrorObject(apiErrorDetails);

            await this.sendError(errorObject);


        } catch (trackingError) {
            logger.warning("ERROR_TRACKER", "Failed to track API error", {
                originalError: apiErrorDetails.message,
                trackingError: trackingError.message,
                url: apiErrorDetails.url,
                status: trackingError.response?.status
            });
        } finally {
            this.isProcessing = false;
        }
    }

    // Helper methods for different types of API errors
    async trackNetworkError(error, requestDetails = {}) {
        return this.trackApiError({
            message: error.message || "Network request failed",
            code: error.code,
            status: error.response?.status || 0,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            url: requestDetails.url,
            method: requestDetails.method,
            requestId: requestDetails.requestId,
            duration: requestDetails.duration,
            attempts: requestDetails.attempts,
            timeout: requestDetails.timeout,
            curl: requestDetails.curl,
            headers: requestDetails.headers,
            requestData: requestDetails.requestData,
            component: requestDetails.component,
            // stack: error.stack,
            maxRetries: requestDetails.maxRetries,
            shouldRetry: requestDetails.shouldRetry,
            retryDelay: requestDetails.retryDelay,
        });
    }

    async trackTimeoutError(requestDetails = {}) {
        return this.trackApiError({
            message: "Request timeout",
            code: "ECONNABORTED",
            status: 0,
            statusText: "Timeout",
            url: requestDetails.url,
            method: requestDetails.method,
            requestId: requestDetails.requestId,
            duration: requestDetails.duration,
            attempts: requestDetails.attempts,
            timeout: requestDetails.timeout,
            curl: requestDetails.curl,
            headers: requestDetails.headers,
            requestData: requestDetails.requestData,
            component: requestDetails.component,
        });
    }

    async trackRetryError(error, requestDetails = {}) {
        return this.trackApiError({
            message: `Request failed after retries: ${error.message}`,
            code: error.code,
            status: error.response?.status || 0,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            url: requestDetails.url,
            method: requestDetails.method,
            requestId: requestDetails.requestId,
            duration: requestDetails.duration,
            attempts: requestDetails.attempts,
            timeout: requestDetails.timeout,
            curl: requestDetails.curl,
            headers: requestDetails.headers,
            requestData: requestDetails.requestData,
            component: requestDetails.component,
            stack: error.stack,
            maxRetries: requestDetails.maxRetries,
        });
    }

    // Configuration methods
    enable() {
        this.config.enabled = true;
        logger.info("ERROR_TRACKER", "API error tracking enabled");
    }

    disable() {
        this.config.enabled = false;
        logger.info("ERROR_TRACKER", "API error tracking disabled");
    }

    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
        logger.info("ERROR_TRACKER", "Configuration updated", newConfig);
    }

    getStatus() {
        return {
            enabled: this.config.enabled,
            isProcessing: this.isProcessing,
            config: this.config
        };
    }

    // Cleanup method
    async destroy() {
        logger.info("ERROR_TRACKER", "Destroying error tracker");
        this.isProcessing = false;
    }

    // Get statistics
    getStatistics() {
        return {
            isProcessing: this.isProcessing,
            enabled: this.config.enabled,
            config: this.config
        };
    }
}

// Create singleton instance
const errorTracker = new ErrorTracker();

// Export main tracking functions
export const trackApiError = (apiErrorDetails) => errorTracker.trackApiError(apiErrorDetails);
export const trackNetworkError = (error, requestDetails) => errorTracker.trackNetworkError(error, requestDetails);
export const trackTimeoutError = (requestDetails) => errorTracker.trackTimeoutError(requestDetails);
export const trackRetryError = (error, requestDetails) => errorTracker.trackRetryError(error, requestDetails);

// Export configuration functions
export const enableApiErrorTracking = () => errorTracker.enable();
export const disableApiErrorTracking = () => errorTracker.disable();
export const updateApiErrorTrackingConfig = (config) => errorTracker.updateConfig(config);
export const getApiErrorTrackingStatus = () => errorTracker.getStatus();
export const getApiErrorTrackingStats = () => errorTracker.getStatistics();

export default errorTracker;